import {
	forkJoin, Observable, of, Subject,
} from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';

import {
	getExpiredSessionMessage,
	isKodieIntroClosed,
	SlLEContext,
} from '../../global-constants';
import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import { SlNavigationService } from '../sl-navigation.service';
import { SlActions } from '../ActionsService/sl-actions';
import {
	ActionType,
	CurrentPageType,
	ModalKeys,
	OnboardingState,
	RequestType,
	TrackActionName,
} from '../../global-interface';
import { IFlashMessageTypes } from '../../../../shared/public/SlFlashMessage/SlFlashMessage';
import { getInitialServerSideData } from '../../../../shared/public/SlUtils/getInitialServerSideData';
import { IError, ErrorCodes } from '../../../../api/private/global.interface';
import { ILandingPageResponse } from '../../../../api/private/learn-engine-api/landing-page-api.interface';
import { IMaterialHeader, LocationType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { SlErrorHandlerService } from '../../../../api/public/error-handling';
import { IUserSettingsResponse } from '../../../../api/public/user-settings-api';
import {
	Service, Container, AuthService, BitService, StorageService, LearningExperienceType, BitLocationType, IUserSetting, history,
} from '../../../../symphony';

@Service()
export class SlCommonService {
	private api = Container.take(SlLEContext, SlApiService);

	private data = Container.take(SlLEContext, SlDataService);

	private actions$ = Container.take(SlLEContext, SlActions).actions$;

	private navigation$ = Container.take(SlLEContext, SlNavigationService)
		.navigation$;

	private handleError = Container.take(SlLEContext, SlErrorHandlerService)
		.handleError;

	private navigationService = Container.take(
		SlLEContext,
		SlNavigationService,
	);

	private authService = Container.take('global', AuthService);

	private bitService = Container.take('global', BitService);

	private storageService = Container.take('global', StorageService);

	public unsubscriber: Subject<void> = Container.take(
		SlLEContext,
		'unsubscriber',
	);

	public initializeApp = (): Observable<null> => of(null);

	public startOnboarding = (): Observable<null> => {
		this.data.onboarding$.next(OnboardingState.InProgress);
		return of(null);
	};

	public completeOnboarding = (): Observable<null> => {
		this.data.onboarding$.next(OnboardingState.Done);
		return of(null);
	};

	public startLearning = (): Observable<null> => {
		window.scrollTo({ top: 0, behavior: 'smooth' });

		return of(null);
	};

	public navigateToUserLogin = (): Observable<null> => {
		history.push(
			`/users/login?returnUrl=${encodeURIComponent(
				`${location.pathname}`,
			)}`,
		);
		return of(null);
	};

	public navigateToPro = (
		source: string,
		item: string,
		returnUrl?: string,
	): Observable<null> => {
		const path = `/payments/${item ? `checkout/item/${item}` : 'pro'}`;
		history.push(`${path}?source=${source}&returnUrl=${encodeURI(returnUrl || window.location.pathname)}`);
		return of(null);
	};

	public onLandingSignup = (isNewRegistered: boolean): Observable<null> => {
		const { currentPage, courseAlias, pathAlias } = this.data.routeData$.value;
		this.actions$.next({
			type: ActionType.UpdateDataAfterLogin,
			requestType: RequestType.Parallel,
		});

		if (isNewRegistered) {
			history.push(
				`/onboarding?landing-page-alias=${currentPage === CurrentPageType.PathPage
					? pathAlias
					: courseAlias
				}`,
			);
		} else {
			this.navigation$.next({
				toLEPage: true,
				query: `?location=${LocationType.LandingPage}`,
			});
		}
		return of(null);
	};

	public expiredSessionIdError = (): void => {
		this.navigation$.next({ toLEPage: true });
		this.data.flashMessage$.next({
			type: IFlashMessageTypes.ERROR,
			message: getExpiredSessionMessage(),
		});
	};

	private getCourseStructureHandleError = (err: IError[]): void => {
		if (!Array.isArray(err)) {
			this.navigationService.navigation$.next({
				toNotFoundCourse: true,
			});
			return;
		}
		this.handleError(err, {
			[ErrorCodes.BadRequest]: () => {
				this.navigationService.navigation$.next({
					toNotFoundCourse: true,
				});
			},
			[ErrorCodes.NotFound]: () => {
				this.navigationService.navigation$.next({
					toNotFoundCourse: true,
				});
			},
		});
	};

	private getLandingPageHandleError = (err: IError[]) => {
		if (!Array.isArray(err)) {
			this.navigationService.navigation$.next({
				toNotFoundCourse: true,
			});
			return;
		}
		this.handleError(err, {
			[ErrorCodes.BadRequest]: () => {
				this.navigationService.navigation$.next({
					toNotFoundCourse: true,
				});
			},
			[ErrorCodes.NotFound]: () => {
				this.navigationService.navigation$.next({
					toNotFoundCourse: true,
				});
			},
		});
	};

	public updateDataAfterLogin = () => {
		this.actions$.next({
			type: ActionType.GetHeartConfigs,
			requestType: RequestType.Parallel,
		});
		return of(null);
	};

	public navigateToLeaderboard = (): Observable<null> => {
		history.push('/leaderboard-league');
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName:
						TrackActionName.OnLeaderboardScreenGoToLeaderboardClick,
				},
			},
		});
		return of(null);
	};

	public getLandingPageData = (
		alias: string,
	): Observable<ILandingPageResponse> => {
		const { landingPageData$, courseStructure$ } = this.data;

		const ssrLandingPage = getInitialServerSideData('ssrLandingPage');

		if (ssrLandingPage) {
			landingPageData$.next(ssrLandingPage?.mainData);
			courseStructure$.next(ssrLandingPage?.courseData);
			return of(null);
		}

		const type = window.location.pathname.split('/')[3] === 'learning-paths'
			? LearningExperienceType.LearningPath
			: LearningExperienceType.Course;

		forkJoin([
			this.api.getLandingPage(alias),
			this.api.getCourseStructure(alias, type),
		])
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: ([lp, course]) => {
					if (lp && course) {
						this.data.landingPageData$.next(lp);
						this.data.courseStructure$.next(course);
					}
					return of(null);
				},
				error: ([err1, err2]) => {
					this.getCourseStructureHandleError(err1);
					this.getLandingPageHandleError(err2);
					return of(null);
				},
			});
		return of(null);
	};

	public openModal = (id: ModalKeys): Observable<null> => {
		const { modals$ } = this.data;
		modals$.next({
			...modals$.value,
			[id]: true,
		});
		return of(null);
	};

	public closeModal = (id: ModalKeys): Observable<null> => {
		const { modals$ } = this.data;
		modals$.next({
			...modals$.value,
			[id]: false,
		});
		return of(null);
	};

	public setBitLocation = (
		locationType: BitLocationType,
		locationDetails: string,
		materialHeader: IMaterialHeader,
	): Observable<null> => {
		this.bitService.setLocation(locationType, locationDetails, materialHeader);
		return of(null);
	};

	public decreaseBitsCount = (
		currentValue: number,
		amount: number,
	): Observable<null> => {
		const { bits$ } = this.bitService;
		if (bits$.value.bits !== currentValue - amount) {
			bits$.next({
				...bits$.value,
				bits: currentValue - amount,
			});
		}
		return of(null);
	};

	public getUserSettings = (): Observable<IUserSettingsResponse> => this.api.getUserSettings().pipe(
		tap(({ data }) => {
			this.data.userSettings$.next(data.userSettings);
		}),
		catchError(() => of(null)),
	);

	public updateUserSettings = (
		setting: IUserSetting,
	): Observable<IUserSettingsResponse> => {
		const updatedUserSettings: IUserSetting[] = [
			...(this.data.userSettings$.value || []),
			setting,
		];
		return this.api.updateUserSettings(updatedUserSettings).pipe(
			tap(() => {
				this.data.userSettings$.next(updatedUserSettings);
			}),
			catchError(() => of(null)),
		);
	};

	public setStreakGoal = (goalDays: number): Observable<null> => this.api.setStreakGoal(this.authService.getUser().id, goalDays);

	public kodieIntroClose = (): Observable<null> => {
		this.storageService.save(isKodieIntroClosed, true, 'local');
		this.data.kodieIntroIsClosed$.next(true);

		return of(null);
	};

	public handleKodieInNavigationAnimationEnd = (): Observable<null> => {
		this.data.isKodieInNavigationAnimationShowed$.next(true);

		return of(null);
	};

	public handleSeeOriginalLanguageButtonClick = () => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnShowOriginalButtonClick,
				},
			},
		});
		this.updateSelectedLocale(
			this.data.course$.value.materialInfo.defaultLanguage,
		);

		return of(null);
	};

	public handleSeeTranslatedLanguageButtonClick = (locale: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnSeeTranslationButtonClick,
				},
			},
		});
		this.updateSelectedLocale(locale);

		return of(null);
	};

	public handleCourseTranslation = (locale?: string): Observable<null> => {
		this.data.isTranslationModalOpened$.next(false);
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnTranslateButtonClick,
					locale,
				},
			},
		});
		this.updateSelectedLocale(locale);

		return of(null);
	};

	public updateSelectedLocale = (
		locale: string,
		isFromCourseContainer = false,
	) => {
		this.data.loading$.next({ courseTranslation: true });
		this.api
			.updateSelectedLocale(
				locale,
				this.data.course$.value?.materialInfo?.header?.alias,
			)
			.pipe(
				tap(() => {
					if (!isFromCourseContainer) {
						this.actions$.next({
							type: ActionType.SetLessonData,
							requestType: RequestType.Parallel,
							data: {
								id: this.data.routeData$.value.materialId,
								lessonPageIndex:
									this.data.routeData$.value.lessonPageIndex,
								isTranslating: true,
							},
						});
					}
					const { pathAlias, courseAlias } = this.data.routeData$.value;
					this.actions$.next({
						type: pathAlias
							? ActionType.GetLearningPathSubtree
							: ActionType.GetCourseSubtree,
						requestType: RequestType.Parallel,
						data: {
							alias: pathAlias || courseAlias,
						},
					});
				}),
				catchError(() => {
					this.data.loading$.next(null);
					return of(null);
				}),
			)
			.subscribe(() => { });
	};

	public handleCloseTranslationModalClick = () => {
		this.data.isTranslationModalOpened$.next(false);
	};

	public translationWarningCtaClick = (isTranslated: boolean) => {
		this.data.isTranslationModalOpened$.next(true);
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnDynamicTranslationTipCTAClick,
					isTranslated,
				},
			},
		});
	};

	public navigateToAICoursePaywall = (): void => {
		// TODO: Temporary solution, find something better
		const returnUrl = window.location.pathname.split('/').filter((_, i) => i < 5).join('/');
		this.navigateToPro(
			'ai-course',
			'ai_course',
			returnUrl,
		);
	};
}
